/**
 * AwsServiceIcon
 * ==============
 * Renders the official AWS Architecture service icon for a given service key,
 * replacing the generic emojis previously used in service section titles.
 *
 * Each icon follows the official AWS Architecture Icon convention: a rounded
 * square tile in the service's product-category gradient/colour with a white
 * resource glyph. Icons are inline SVG (no external asset bundle, no network
 * dependency, crisp at any size, themeable).
 *
 * AWS Architecture Icons are provided by Amazon under the AWS Trademark &
 * Architecture Icon guidelines (https://aws.amazon.com/architecture/icons/).
 * They are reproduced here for identification of AWS services within KONE iCOP
 * and are not modified to imply AWS endorsement.
 *
 * Usage:
 *   <AwsServiceIcon service="lambda" />
 *   <AwsServiceIcon service="ec2" size={28} />
 *
 * Unknown service keys fall back to a neutral AWS-navy tile with a cloud glyph,
 * so a missing mapping degrades gracefully rather than throwing.
 */

import React from 'react';
import './AwsServiceIcon.css';

// AWS product-category colours (from the official icon palette).
const CATEGORY = {
  compute: '#ED7100',       // orange
  storage: '#7AA116',       // green
  database: '#527FFF',      // blue
  networking: '#8C4FFF',    // purple
  security: '#DD344C',      // red
  management: '#E7157B',    // pink/magenta
  analytics: '#8C4FFF',     // purple
  appIntegration: '#E7157B',// pink
  containers: '#ED7100',    // orange
  mlOrGeneric: '#232F3E',   // AWS navy (fallback)
};

/**
 * Per-service definition: the tile colour (by product category) and the white
 * resource glyph (SVG path/elements drawn on a 24x24 user space, white fill).
 * Glyphs are simplified line/solid marks evoking each official resource icon.
 */
const SERVICES = {
  lambda: {
    label: 'AWS Lambda',
    color: CATEGORY.compute,
    glyph: <path d="M4 19 L10.5 6 L13 6 L8 16 L15 16 L13.5 19 Z M14 6 L16.5 6 L21 19 L18.3 19 Z" />,
  },
  ec2: {
    label: 'Amazon EC2',
    color: CATEGORY.compute,
    glyph: (
      <>
        <rect x="6" y="6" width="12" height="12" rx="1.5" fill="none" stroke="#fff" strokeWidth="1.6" />
        <rect x="9" y="9" width="6" height="6" rx="0.5" />
        <path d="M8 4 v2 M12 4 v2 M16 4 v2 M8 18 v2 M12 18 v2 M16 18 v2 M4 8 h2 M4 12 h2 M4 16 h2 M18 8 h2 M18 12 h2 M18 16 h2" stroke="#fff" strokeWidth="1.4" />
      </>
    ),
  },
  ecs: {
    label: 'Amazon ECS',
    color: CATEGORY.containers,
    glyph: <path d="M4 7 h7 v4 H4 Z M13 7 h7 v4 h-7 Z M4 13 h7 v4 H4 Z M13 13 h7 v4 h-7 Z" />,
  },
  ecr: {
    label: 'Amazon ECR',
    color: CATEGORY.containers,
    glyph: (
      <>
        <path d="M12 3 L20 7 V17 L12 21 L4 17 V7 Z" fill="none" stroke="#fff" strokeWidth="1.6" />
        <path d="M12 7 l4 2 v4 l-4 2 l-4-2 V9 Z" />
      </>
    ),
  },
  s3: {
    label: 'Amazon S3',
    color: CATEGORY.storage,
    glyph: (
      <>
        <path d="M6 5 h12 l-1 14 a1 1 0 0 1 -1 1 H8 a1 1 0 0 1 -1 -1 Z" fill="none" stroke="#fff" strokeWidth="1.6" />
        <path d="M6 9 h12 M6.5 14 h11" stroke="#fff" strokeWidth="1.4" />
      </>
    ),
  },
  rds: {
    label: 'Amazon RDS',
    color: CATEGORY.database,
    glyph: (
      <>
        <ellipse cx="12" cy="6" rx="7" ry="2.6" fill="none" stroke="#fff" strokeWidth="1.6" />
        <path d="M5 6 V18 c0 1.5 3.1 2.6 7 2.6 s7 -1.1 7 -2.6 V6" fill="none" stroke="#fff" strokeWidth="1.6" />
        <path d="M5 12 c0 1.5 3.1 2.6 7 2.6 s7 -1.1 7 -2.6" fill="none" stroke="#fff" strokeWidth="1.4" />
      </>
    ),
  },
  dynamodb: {
    label: 'Amazon DynamoDB',
    color: CATEGORY.database,
    glyph: (
      <>
        <ellipse cx="12" cy="6" rx="7" ry="2.6" fill="none" stroke="#fff" strokeWidth="1.6" />
        <path d="M5 6 V18 c0 1.5 3.1 2.6 7 2.6 s7 -1.1 7 -2.6 V6" fill="none" stroke="#fff" strokeWidth="1.6" />
        <path d="M9 13 l2 2 l4 -4" fill="none" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ),
  },
  elasticache: {
    label: 'Amazon ElastiCache',
    color: CATEGORY.database,
    glyph: <path d="M5 7 h3 v10 H5 Z M10.5 7 h3 v10 h-3 Z M16 7 h3 v10 h-3 Z" />,
  },
  apigateway: {
    label: 'Amazon API Gateway',
    color: CATEGORY.networking,
    glyph: (
      <>
        <circle cx="7" cy="12" r="2.4" />
        <circle cx="17" cy="7" r="2.4" />
        <circle cx="17" cy="17" r="2.4" />
        <path d="M9.2 11 L15 7.6 M9.2 13 L15 16.4" stroke="#fff" strokeWidth="1.5" />
      </>
    ),
  },
  vpc: {
    label: 'Amazon VPC',
    color: CATEGORY.networking,
    glyph: (
      <>
        <rect x="4" y="5" width="16" height="14" rx="2" fill="none" stroke="#fff" strokeWidth="1.6" />
        <circle cx="9" cy="12" r="2" />
        <circle cx="15" cy="12" r="2" />
        <path d="M11 12 h2" stroke="#fff" strokeWidth="1.4" />
      </>
    ),
  },
  alb: {
    label: 'Application Load Balancer',
    color: CATEGORY.networking,
    glyph: (
      <>
        <circle cx="12" cy="5" r="2.2" />
        <circle cx="6" cy="18" r="2.2" />
        <circle cx="12" cy="18" r="2.2" />
        <circle cx="18" cy="18" r="2.2" />
        <path d="M12 7 V13 M12 13 L6 16 M12 13 L12 16 M12 13 L18 16" fill="none" stroke="#fff" strokeWidth="1.5" />
      </>
    ),
  },
  elb: {
    label: 'Elastic Load Balancing',
    color: CATEGORY.networking,
    glyph: (
      <>
        <circle cx="12" cy="5" r="2.2" />
        <circle cx="6" cy="18" r="2.2" />
        <circle cx="12" cy="18" r="2.2" />
        <circle cx="18" cy="18" r="2.2" />
        <path d="M12 7 V13 M12 13 L6 16 M12 13 L12 16 M12 13 L18 16" fill="none" stroke="#fff" strokeWidth="1.5" />
      </>
    ),
  },
  cloudfront: {
    label: 'Amazon CloudFront',
    color: CATEGORY.networking,
    glyph: (
      <>
        <circle cx="12" cy="12" r="8" fill="none" stroke="#fff" strokeWidth="1.6" />
        <path d="M4 12 h16 M12 4 c3 3 3 13 0 16 M12 4 c-3 3 -3 13 0 16" fill="none" stroke="#fff" strokeWidth="1.3" />
      </>
    ),
  },
  cloudwatch: {
    label: 'Amazon CloudWatch',
    color: CATEGORY.management,
    glyph: (
      <>
        <circle cx="12" cy="12" r="8" fill="none" stroke="#fff" strokeWidth="1.6" />
        <path d="M12 7 V12 L15.5 14" fill="none" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
      </>
    ),
  },
  config: {
    label: 'AWS Config',
    color: CATEGORY.management,
    glyph: (
      <>
        <circle cx="12" cy="12" r="3" fill="none" stroke="#fff" strokeWidth="1.6" />
        <path d="M12 3 v3 M12 18 v3 M3 12 h3 M18 12 h3 M5.6 5.6 l2.1 2.1 M16.3 16.3 l2.1 2.1 M18.4 5.6 l-2.1 2.1 M7.7 16.3 l-2.1 2.1" stroke="#fff" strokeWidth="1.5" />
      </>
    ),
  },
  eventbridge: {
    label: 'Amazon EventBridge',
    color: CATEGORY.appIntegration,
    glyph: (
      <>
        <rect x="4" y="5" width="16" height="14" rx="2" fill="none" stroke="#fff" strokeWidth="1.6" />
        <path d="M4 9 h16 M9 5 v-2 M15 5 v-2 M9 13 l2 2 l4 -4" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ),
  },
  sns: {
    label: 'Amazon SNS',
    color: CATEGORY.appIntegration,
    glyph: <path d="M5 10 l6 -5 v14 l-6 -5 Z M13 7 a5 5 0 0 1 0 10" fill="#fff" stroke="#fff" strokeWidth="1.3" />,
  },
  sqs: {
    label: 'Amazon SQS',
    color: CATEGORY.appIntegration,
    glyph: <path d="M4 8 h4 v8 H4 Z M10 8 h4 v8 h-4 Z M16 8 h4 v8 h-4 Z" />,
  },
  stepfunctions: {
    label: 'AWS Step Functions',
    color: CATEGORY.appIntegration,
    glyph: (
      <>
        <rect x="4" y="4" width="7" height="5" rx="1" />
        <rect x="13" y="10" width="7" height="5" rx="1" />
        <rect x="4" y="16" width="7" height="4" rx="1" />
        <path d="M11 6.5 h2 v6 M13 12.5 h-2 v5.5" fill="none" stroke="#fff" strokeWidth="1.3" />
      </>
    ),
  },
  kinesis: {
    label: 'Amazon Kinesis',
    color: CATEGORY.analytics,
    glyph: <path d="M4 8 c3 -3 5 3 8 0 s5 -3 8 0 M4 14 c3 -3 5 3 8 0 s5 -3 8 0" fill="none" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" />,
  },
  glue: {
    label: 'AWS Glue',
    color: CATEGORY.analytics,
    glyph: (
      <>
        <circle cx="8" cy="8" r="3" fill="none" stroke="#fff" strokeWidth="1.6" />
        <circle cx="16" cy="16" r="3" fill="none" stroke="#fff" strokeWidth="1.6" />
        <path d="M10 10 L14 14" stroke="#fff" strokeWidth="1.8" />
      </>
    ),
  },
  mwaa: {
    label: 'Amazon MWAA',
    color: CATEGORY.analytics,
    glyph: (
      <>
        <circle cx="6" cy="12" r="2" />
        <circle cx="12" cy="6" r="2" />
        <circle cx="18" cy="12" r="2" />
        <circle cx="12" cy="18" r="2" />
        <path d="M7.5 11 L11 7 M13 7 L16.5 11 M16.5 13 L13 17 M11 17 L7.5 13" stroke="#fff" strokeWidth="1.4" />
      </>
    ),
  },
  guardduty: {
    label: 'Amazon GuardDuty',
    color: CATEGORY.security,
    glyph: (
      <>
        <path d="M12 3 L19 6 V11 c0 5 -3 8 -7 10 c-4 -2 -7 -5 -7 -10 V6 Z" fill="none" stroke="#fff" strokeWidth="1.6" />
        <circle cx="12" cy="11" r="2.2" />
        <path d="M12 13 v3" stroke="#fff" strokeWidth="1.5" />
      </>
    ),
  },
  waf: {
    label: 'AWS WAF',
    color: CATEGORY.security,
    glyph: (
      <>
        <path d="M12 3 L19 6 V11 c0 5 -3 8 -7 10 c-4 -2 -7 -5 -7 -10 V6 Z" fill="none" stroke="#fff" strokeWidth="1.6" />
        <path d="M8.5 11.5 l2.5 2.5 l4.5 -5" fill="none" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ),
  },
  secretsmanager: {
    label: 'AWS Secrets Manager',
    color: CATEGORY.security,
    glyph: (
      <>
        <rect x="5" y="10" width="14" height="9" rx="1.5" fill="none" stroke="#fff" strokeWidth="1.6" />
        <path d="M8 10 V7.5 a4 4 0 0 1 8 0 V10" fill="none" stroke="#fff" strokeWidth="1.6" />
        <circle cx="12" cy="14.5" r="1.6" />
      </>
    ),
  },
  parameterstore: {
    label: 'SSM Parameter Store',
    color: CATEGORY.management,
    glyph: (
      <>
        <rect x="4" y="5" width="16" height="14" rx="2" fill="none" stroke="#fff" strokeWidth="1.6" />
        <path d="M7 9 l2 2 l-2 2 M11 13 h3" fill="none" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ),
  },
  backup: {
    label: 'AWS Backup',
    color: CATEGORY.storage,
    glyph: (
      <>
        <circle cx="12" cy="12" r="8" fill="none" stroke="#fff" strokeWidth="1.6" />
        <path d="M12 7 V12 l3.5 2" fill="none" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M6 5 v3 h3" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ),
  },
  codepipeline: {
    label: 'AWS CodePipeline',
    color: CATEGORY.management,
    glyph: (
      <>
        <circle cx="6" cy="12" r="2.2" />
        <circle cx="18" cy="12" r="2.2" />
        <path d="M8.2 12 h7.6" fill="none" stroke="#fff" strokeWidth="1.6" />
        <path d="M14 9.5 l2 2.5 l-2 2.5" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ),
  },
  msk: {
    label: 'Amazon MSK',
    color: CATEGORY.analytics,
    glyph: (
      <>
        <circle cx="7" cy="12" r="2" />
        <circle cx="17" cy="7" r="2" />
        <circle cx="17" cy="17" r="2" />
        <path d="M9 11 L15 7.5 M9 13 L15 16.5" stroke="#fff" strokeWidth="1.5" />
      </>
    ),
  },
  // Extended coverage (DMI cost-table services)
  acm: {
    label: 'AWS Certificate Manager',
    color: CATEGORY.security,
    glyph: (
      <>
        <rect x="4" y="6" width="16" height="11" rx="1.5" fill="none" stroke="#fff" strokeWidth="1.6" />
        <path d="M7 10 h10 M7 13 h6" stroke="#fff" strokeWidth="1.4" />
        <circle cx="16" cy="16.5" r="3" fill="none" stroke="#fff" strokeWidth="1.4" />
        <path d="M14.6 20 l1 2 l1-1 l1 1 l1-2" fill="none" stroke="#fff" strokeWidth="1.2" />
      </>
    ),
  },
  accessanalyzer: {
    label: 'IAM Access Analyzer',
    color: CATEGORY.security,
    glyph: (
      <>
        <circle cx="10.5" cy="10.5" r="5" fill="none" stroke="#fff" strokeWidth="1.7" />
        <path d="M14.5 14.5 L20 20" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" />
        <path d="M8 10.5 h5 M10.5 8 v5" stroke="#fff" strokeWidth="1.3" />
      </>
    ),
  },
  kms: {
    label: 'AWS Key Management Service',
    color: CATEGORY.security,
    glyph: (
      <>
        <circle cx="8" cy="9" r="3.5" fill="none" stroke="#fff" strokeWidth="1.7" />
        <path d="M10.5 11 L19 19 M16 16 l2 -2 M14 14 l1.5 1.5" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" />
      </>
    ),
  },
  opensearch: {
    label: 'Amazon OpenSearch Service',
    color: CATEGORY.analytics,
    glyph: (
      <>
        <circle cx="10" cy="10" r="5" fill="none" stroke="#fff" strokeWidth="1.7" />
        <path d="M13.6 13.6 L20 20" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" />
        <path d="M10 7 a3 3 0 0 1 3 3" fill="none" stroke="#fff" strokeWidth="1.3" strokeLinecap="round" />
      </>
    ),
  },
  inspector: {
    label: 'Amazon Inspector',
    color: CATEGORY.security,
    glyph: (
      <>
        <path d="M12 3 L19 6 V12 C19 16.5 16 19.5 12 21 C8 19.5 5 16.5 5 12 V6 Z" fill="none" stroke="#fff" strokeWidth="1.6" />
        <path d="M9 12 l2 2 l4 -4.5" fill="none" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ),
  },
  transfer: {
    label: 'AWS Transfer Family',
    color: CATEGORY.networking,
    glyph: (
      <>
        <path d="M5 9 h11 M12 5 l4 4 l-4 4" fill="none" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M19 15 h-11 M12 11 l-4 4 l4 4" fill="none" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ),
  },
  appflow: {
    label: 'Amazon AppFlow',
    color: CATEGORY.analytics,
    glyph: (
      <>
        <circle cx="6" cy="12" r="2.2" />
        <circle cx="18" cy="7" r="2.2" />
        <circle cx="18" cy="17" r="2.2" />
        <path d="M8 11 L16 7.5 M8 13 L16 16.5" fill="none" stroke="#fff" strokeWidth="1.6" />
      </>
    ),
  },
  securityhub: {
    label: 'AWS Security Hub',
    color: CATEGORY.security,
    glyph: (
      <>
        <path d="M12 3 L19 6 V12 C19 16.5 16 19.5 12 21 C8 19.5 5 16.5 5 12 V6 Z" fill="none" stroke="#fff" strokeWidth="1.6" />
        <circle cx="12" cy="11" r="2" />
        <path d="M12 13 v4 M10 15 h4" stroke="#fff" strokeWidth="1.4" />
      </>
    ),
  },
  dms: {
    label: 'AWS Database Migration Service',
    color: CATEGORY.database,
    glyph: (
      <>
        <ellipse cx="7" cy="7" rx="3.5" ry="1.8" fill="none" stroke="#fff" strokeWidth="1.5" />
        <path d="M3.5 7 v4 c0 1 1.6 1.8 3.5 1.8 s3.5 -.8 3.5 -1.8 V7" fill="none" stroke="#fff" strokeWidth="1.5" />
        <path d="M12 12 h6 M15 9 l3 3 l-3 3" fill="none" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ),
  },
};

const FALLBACK = {
  label: 'AWS',
  color: CATEGORY.mlOrGeneric,
  glyph: <path d="M6 15 a4 4 0 0 1 1 -7.9 a5 5 0 0 1 9.6 1 A3.5 3.5 0 0 1 17 15 Z" fill="none" stroke="#fff" strokeWidth="1.6" />,
};

const AwsServiceIcon = ({ service, size = 22, className = '', title }) => {
  const def = SERVICES[service] || FALLBACK;
  const label = title || def.label;
  return (
    <span
      className={`aws-service-icon ${className}`.trim()}
      role="img"
      aria-label={label}
      title={label}
      style={{ width: size, height: size }}
    >
      <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" focusable="false">
        <rect x="0" y="0" width="24" height="24" rx="4.5" fill={def.color} />
        <g fill="#fff">{def.glyph}</g>
      </svg>
    </span>
  );
};

export default AwsServiceIcon;
